import React, { useState, ChangeEvent } from 'react'
import {
  Button,
  Icon,
  WithPopover,
  Popover,
  Scrollable,
  TextInputWithIcon,
  FieldLabel,
  FieldValue,
} from '@lightspeed/design-system-react'
import { ApiUser } from '@vend/types'

import { T, useT } from '@transifex/react'
import { useRegisterManager } from '../../../ng-compat/hooks'

import { LineItemUser } from '../../../models/line-item'
import { SalesPerson, User } from './SalesPerson'
interface Props {
  selectedUser: LineItemUser | null
  onSelect: (user: LineItemUser) => void
  loggedInUser: ApiUser
}

const convertApiUserToUser = (apiUser: ApiUser): User => {
  return {
    displayName: apiUser.display_name,
    username: apiUser.username,
    id: apiUser.id,
    email: apiUser.email || '',
    images: apiUser.images,
  }
}

const containsFilter = (user: ApiUser, filter: string): boolean => {
  const lowerCaseUser = {
    username: user.username.toLowerCase(),
    displayName: user.display_name.toLowerCase(),
    email: user.email?.toLowerCase(),
  }
  const lowerCaseFilter = filter.toLowerCase()

  if (
    !lowerCaseUser.username.includes(lowerCaseFilter) &&
    !lowerCaseUser.displayName.includes(lowerCaseFilter) &&
    !lowerCaseUser.email?.includes(lowerCaseFilter)
  ) {
    return false
  }
  return true
}

export const SalesPersonSelect: React.FC<Props> = ({
  selectedUser,
  onSelect,
  loggedInUser,
}) => {
  const t = useT()
  const salesPersonList = useRegisterManager().getAllUsers() || []
  // Use logged in user if line item has no explicit sold by user
  const displayUser: LineItemUser =
    selectedUser || (convertApiUserToUser(loggedInUser) as LineItemUser)
  // Should not show deleted and inactive user
  const filteredSalesPersonList = salesPersonList.filter(
    user => user.deleted_at === null && user.enabled
  )

  const [isPopoverOpen, toggleIsPopoverOpen] = useState<boolean>(false)
  const [filterStr, setFilterStr] = useState<string>('')

  const showPopover = () => {
    toggleIsPopoverOpen(true)
  }
  const hidePopover = () => {
    toggleIsPopoverOpen(false)
    setFilterStr('')
  }
  const onSelectHandler = (user: LineItemUser) => {
    onSelect(user)
    hidePopover()
  }

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value || ''
    setFilterStr(value)
  }

  return (
    <WithPopover
      insideModal={true}
      className="vd-popover--with-list"
      direction="left"
    >
      <Button
        modifier="text"
        variant="supplementary"
        onClick={showPopover}
        disabled={filteredSalesPersonList.length === 0}
        className="vd-flex vd-flex--align-center vd-flex--justify-left"
        data-track="sales-person-select"
      >
        <Icon icon="fa-pencil" className="vd-pr1" />
        <T _str={`Sold by {userName}`} userName={displayUser.displayName} />
      </Button>
      {isPopoverOpen && (
        <Popover
          onClose={hidePopover}
          className="sales-person-popover-container"
        >
          <div className="vd-pt5 vd-pb5 vd-pl6 vd-pr6">
            <FieldLabel
              label={t('Choose from users in your outlet')}
              htmlFor="user-search"
            />
            <FieldValue>
              <TextInputWithIcon
                placeholder={t('Search for a user')}
                icon="fa-search"
                iconPosition="left"
                type="text"
                autoFocus={true}
                id="user-search"
                onChange={onSearch}
              />
            </FieldValue>
          </div>
          <Scrollable>
            <ol className="vd-popover-list">
              {filteredSalesPersonList.map(user => {
                if (
                  filterStr !== '' &&
                  !containsFilter(user, filterStr.trim())
                ) {
                  // Should not show the user when not matching the search string
                  return null
                }
                return (
                  <SalesPerson
                    key={user.id}
                    user={convertApiUserToUser(user)}
                    active={displayUser.id === user.id}
                    onSelect={user => onSelectHandler(user)}
                    keyword={filterStr.trim()}
                  />
                )
              })}
            </ol>
          </Scrollable>
        </Popover>
      )}
    </WithPopover>
  )
}
